import { ChevronDown, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

interface SettingCardTestIds {
  accentBar: string;
  toggle: string;
  chevronRight: string;
  chevronDown: string;
  preview: string;
  content: string;
}

interface SettingCardProps {
  /** CSS classes applied to the left accent bar (colour + opacity). */
  accentBarClassName: string;
  /** Content rendered in the header row left of the chevron (badge, labels, etc.). */
  headerContent: ReactNode;
  /** Text/node shown in the collapsed preview paragraph. */
  preview: ReactNode;
  /** Content rendered inside the expanded panel. */
  expandedContent: ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  testIds: SettingCardTestIds;
}

export function SettingCard({
  accentBarClassName,
  headerContent,
  preview,
  expandedContent,
  isExpanded,
  onToggle,
  testIds,
}: SettingCardProps) {
  return (
    <div className="relative pl-4">
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-full transition-all duration-300 ${accentBarClassName}`}
        data-testid={testIds.accentBar}
      />

      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left group"
        data-testid={testIds.toggle}
      >
        <span className="space-y-2 block">
          <span className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 flex-wrap">
              {headerContent}
            </span>
            {isExpanded ? (
              <ChevronDown
                className="w-4 h-4 text-secondary-500 group-hover:text-primary-500 transition-colors shrink-0"
                data-testid={testIds.chevronDown}
              />
            ) : (
              <ChevronRight
                className="w-4 h-4 text-secondary-500 group-hover:text-primary-500 transition-all group-hover:translate-x-0.5 shrink-0"
                data-testid={testIds.chevronRight}
              />
            )}
          </span>
          {!isExpanded && (
            <span
              className="text-[0.875rem] leading-5 line-clamp-2 text-secondary-500"
              data-testid={testIds.preview}
            >
              {preview}
            </span>
          )}
        </span>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid={testIds.content}
      >
        <div className="mt-4 space-y-4 pl-0">{expandedContent}</div>
      </div>
    </div>
  );
}
